/**
 * The client half of the realtime channel.
 *
 * ## The token goes in the first message, never in the URL
 *
 * `new WebSocket(url)` cannot set headers, which is exactly why the temptation is to put
 * the access token in the query string — and why the server refuses to read it from there.
 * A handshake is an ordinary GET, so a `?token=` lands verbatim in the edge access log,
 * where the redaction layer does not look. So this opens the socket with a bare URL and
 * sends `{type:'auth'}` as its first frame.
 *
 * ## Reconnection, and why it backs off
 *
 * A television loses its connection every time it sleeps, and a phone every time it is
 * pocketed. Reconnecting immediately and forever turns a server restart into a room full
 * of devices hammering it at the moment it is least able to cope, so the delay grows and
 * is jittered — two devices that dropped together do not come back in lockstep.
 *
 * A close code of 4001 or 4003 is *not* retried: those mean "your token is no good" and
 * "this device is revoked", and neither improves by asking again. Retrying a revoked
 * device forever is how a revoked device keeps a socket half-open in the server's logs.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { DeviceType, RemoteTracksDto } from '@filmodasi/shared';
import { apiUrl, currentAccessToken, deviceId } from '../api/client';

export type RealtimeStatus = 'idle' | 'connecting' | 'open' | 'closed';

/** What the server sends. A closed set, mirrored from `realtime/server.ts`. */
export type ServerMessage =
  | { type: 'ready'; deviceId: string }
  | { type: 'error'; message: string }
  | { type: 'presence'; devices: PresenceDevice[] }
  | { type: 'pairing:approved' }
  | {
      type: 'state';
      deviceId: string;
      itemId: string | null;
      playbackState: 'playing' | 'paused' | 'stopped';
      positionSec: number | null;
      durationSec: number | null;
      /**
       * What the television can switch between, or null where it cannot say.
       *
       * Optional on the type as well as nullable, because an older television in the
       * household — one still running the previous bundle, which happens for as long as it
       * takes somebody to relaunch the app — sends a state message without this field at
       * all.
       */
      tracks?: RemoteTracksDto | null;
    }
  | {
      type: 'command';
      from: string;
      command: string;
      value: number | null;
      /** Which track to select. Null on a `subtitleTrack` command means off. */
      trackId?: string | null;
    }
  | {
      type: 'playOnTv';
      from: string;
      itemId: string;
      profileId: string;
      positionSec: number | null;
    };

export interface PresenceDevice {
  deviceId: string;
  deviceType: string;
  /** The household's name for it. Null for the moment before its row has been read. */
  name: string | null;
  profileId: string | null;
  connectedAt: number;
}

/** Codes the server uses to say "do not come back". */
const TERMINAL_CLOSE_CODES = new Set([4001, 4003]);
const BASE_RETRY_MS = 1_000;
const MAX_RETRY_MS = 30_000;

/**
 * A short, human label for the management screen — "Windows · Chrome".
 *
 * The raw user agent is the obvious thing to send and the wrong one: the screen exists to
 * be scanned when somebody is deciding which row to revoke, and a hundred characters of
 * `Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko)…`
 * pushes every useful column off the line. Best-effort by nature — an unrecognised agent
 * gets no label rather than a wrong one.
 */
export function platformLabel(): string | undefined {
  const ua = navigator.userAgent;
  const os =
    /Windows/.test(ua) ? 'Windows'
    : /Android/.test(ua) ? 'Android'
    : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Tizen/.test(ua) ? 'Tizen'
    : /Linux/.test(ua) ? 'Linux'
    : null;

  // Order matters: Edge and Opera both contain "Chrome", and Chrome contains "Safari".
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : null;

  const parts = [os, browser].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

function socketUrl(): string {
  const base = apiUrl('/api/realtime');
  if (base.startsWith('http')) return base.replace(/^http/, 'ws');
  // Relative on the web build, where the API shares an origin with the page.
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}${base}`;
}

export interface Realtime {
  status: RealtimeStatus;
  presence: PresenceDevice[];
  /** Sends a message, or returns false if the socket is not open. */
  send: (payload: Record<string, unknown>) => boolean;
  /**
   * Adds a listener; returns its remover.
   *
   * There is one socket for the whole app, so screens subscribe rather than each opening
   * their own — a connection per screen would announce the device arriving and leaving on
   * every navigation, and the household's presence list would flicker accordingly.
   */
  subscribe: (handler: (message: ServerMessage) => void) => () => void;
}

export function useRealtime({
  enabled,
  profileId,
  deviceType,
  onMessage,
}: {
  /** False on the login screen and wherever there is no session to authenticate with. */
  enabled: boolean;
  profileId: string | null;
  /** Only ever a label on the management screen — nothing branches on it server-side. */
  deviceType: DeviceType;
  onMessage?: (message: ServerMessage) => void;
}): Realtime {
  const [status, setStatus] = useState<RealtimeStatus>('idle');
  const [presence, setPresence] = useState<PresenceDevice[]>([]);

  const socketRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  /*
   * Held in a ref so a caller can pass an inline handler without the connection being torn
   * down and rebuilt on every render — which on a channel like this would mean a reconnect
   * per keystroke on any screen that also has state.
   */
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  const listenersRef = useRef(new Set<(message: ServerMessage) => void>());

  const subscribe = useCallback((handler: (message: ServerMessage) => void) => {
    listenersRef.current.add(handler);
    return () => {
      listenersRef.current.delete(handler);
    };
  }, []);
  const profileRef = useRef(profileId);
  profileRef.current = profileId;
  const deviceTypeRef = useRef(deviceType);
  deviceTypeRef.current = deviceType;

  const send = useCallback((payload: Record<string, unknown>): boolean => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(payload));
    return true;
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStatus('idle');
      return;
    }

    // Set by the cleanup so that a socket which closes *after* the effect is torn down
    // does not schedule a reconnect into an unmounted component.
    let disposed = false;

    const connect = (): void => {
      const token = currentAccessToken();
      if (!token) {
        // No access token yet — the session is still being restored from the refresh
        // cookie. Try again shortly rather than failing the connection outright.
        timerRef.current = window.setTimeout(connect, 500);
        return;
      }

      setStatus('connecting');
      let socket: WebSocket;
      try {
        socket = new WebSocket(socketUrl());
      } catch {
        setStatus('closed');
        return;
      }
      socketRef.current = socket;

      socket.onopen = () => {
        // The first frame, always. Nothing else is accepted until this is answered.
        socket.send(
          JSON.stringify({
            type: 'auth',
            accessToken: currentAccessToken(),
            deviceId: deviceId(),
            profileId: profileRef.current,
            deviceType: deviceTypeRef.current,
            platform: platformLabel(),
          }),
        );
      };

      socket.onmessage = (event) => {
        let message: ServerMessage;
        try {
          message = JSON.parse(String(event.data)) as ServerMessage;
        } catch {
          return;
        }

        if (message.type === 'ready') {
          setStatus('open');
          retryRef.current = 0; // a successful handshake resets the backoff
        }
        if (message.type === 'presence') setPresence(message.devices);

        onMessageRef.current?.(message);
        // A throwing listener must not take the socket down with it — one screen's bug
        // would otherwise stop every other screen receiving anything.
        for (const listener of listenersRef.current) {
          try {
            listener(message);
          } catch {
            /* a listener's problem, not the channel's */
          }
        }
      };

      socket.onclose = (event) => {
        socketRef.current = null;
        setStatus('closed');
        setPresence([]);
        if (disposed || TERMINAL_CLOSE_CODES.has(event.code)) return;

        // Exponential with jitter: devices that dropped together must not return together.
        const attempt = retryRef.current++;
        const delay = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS);
        timerRef.current = window.setTimeout(connect, delay * (0.5 + Math.random()));
      };

      socket.onerror = () => {
        // `close` always follows, and it is where reconnection is decided. Handling both
        // would schedule two reconnects for one failure.
      };
    };

    connect();

    return () => {
      disposed = true;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket) {
        // Detached before closing: otherwise `onclose` fires during teardown and sets
        // state on a component that is going away.
        socket.onopen = socket.onmessage = socket.onclose = socket.onerror = null;
        socket.close(1000, 'client navigating away');
      }
      setStatus('idle');
    };
  }, [enabled]);

  /*
   * A profile switch is told to the server on the existing socket rather than by
   * reconnecting. Reconnecting would drop the household's presence entry and put it back
   * a moment later, which every other device would see as the TV going away.
   */
  useEffect(() => {
    if (status !== 'open') return;
    send({ type: 'state', playbackState: 'stopped', profileId });
  }, [profileId, status, send]);

  return { status, presence, send, subscribe };
}
